/**
 * Platform API
 *
 * Functions for platform-wide admin operations.
 */

import { api } from './client';
import { SubscriptionTier } from './auth';

// =============================================================================
// Types
// =============================================================================

export interface PlatformOrganization {
  id: string;
  name: string;
  slug: string;
  subscriptionTier: SubscriptionTier;
  isActive: boolean;
  createdAt: string;
  lastActivityAt: string;
  memberCount: number;
}

export interface OrgActivityEntry {
  timestamp: string;
  action: string;
  userId: string;
  userEmail?: string;
  orgId: string;
  orgSlug: string;
  userRole?: string;
  method?: string;
  path?: string;
  resourceType?: string;
  resourceId?: string;
  description: string;
  details?: Record<string, unknown>;
  ip?: string;
}

export interface OrgLogsResponse {
  organization: string;
  slug: string;
  hours: number;
  totalLogSize: number;
  totalLogSizeFormatted: string;
  count: number;
  entries: OrgActivityEntry[];
}

export interface OrgAdmin {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  role: 'OWNER' | 'ADMIN';
  lastLoginAt: string | null;
  passwordSetAt: string | null;
}

export interface OrgBillingDetail {
  subscriptionTier: SubscriptionTier;
  /** Tier actually in effect (paid tier vs. an unexpired season pass). */
  effectiveTier: SubscriptionTier;
  subscriptionStatus: string | null;
  /** True = cancelled in the Stripe portal; runs to currentPeriodEnd. */
  cancelAtPeriodEnd: boolean;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  seasonPass: { tier: SubscriptionTier; expiresAt: string } | null;
  hasStripeCustomer: boolean;
  /** Stripe/maintenance ledger, newest first (raw event types). */
  recentEvents: Array<{ type: string; createdAt: string }>;
}

export interface OrgStats {
  members: { owners: number; admins: number; crew: number };
  eventsThisMonth: number;
  eventsMonthLimit: number | null;
  eventsTotal: number;
  messagesLast30d: number;
  qrLinksActive: number;
  qrJoinsTotal: number;
}

export interface PlatformOrganizationDetail extends PlatformOrganization {
  admins: OrgAdmin[];
  billing: OrgBillingDetail;
  stats: OrgStats;
}

export interface PlatformOrganizationsResponse {
  organizations: PlatformOrganization[];
  total: number;
}

export interface CreatePlatformOrganizationInput {
  name: string;
  slug: string;
  description?: string;
  contactEmail?: string;
  ownerEmail: string;
  ownerFirstName: string;
  ownerLastName: string;
  subscriptionTier?: SubscriptionTier;
}

export interface CreatePlatformOrganizationResponse {
  id: string;
  name: string;
  slug: string;
  subscriptionTier: SubscriptionTier;
  isActive: boolean;
  createdAt: string;
  owner: {
    email: string;
    firstName: string;
    lastName: string;
    isNewUser: boolean;
  };
  message: string;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get all organizations for platform admin dashboard
 */
export async function getPlatformOrganizations(): Promise<PlatformOrganizationsResponse> {
  const response = await api.get<PlatformOrganizationsResponse>('/platform/organizations');
  return response.data;
}

/**
 * Get a single organization with its admins/owners
 */
export async function getPlatformOrganization(id: string): Promise<PlatformOrganizationDetail> {
  const response = await api.get<PlatformOrganizationDetail>(`/platform/organizations/${id}`);
  return response.data;
}

/**
 * Create a new organization with an owner account
 */
export async function createPlatformOrganization(
  data: CreatePlatformOrganizationInput
): Promise<CreatePlatformOrganizationResponse> {
  const response = await api.post<CreatePlatformOrganizationResponse>('/platform/organizations', data);
  return response.data;
}

/**
 * Reset a user's password (platform admin only)
 */
export async function platformResetUserPassword(
  userId: string,
  newPassword: string
): Promise<{ message: string }> {
  const response = await api.post<{ message: string }>(`/platform/users/${userId}/reset-password`, { newPassword });
  return response.data;
}

/**
 * Update organization subscription tier
 */
export async function updatePlatformOrganization(
  id: string,
  data: { subscriptionTier: SubscriptionTier }
): Promise<PlatformOrganizationDetail> {
  const response = await api.patch<PlatformOrganizationDetail>(`/platform/organizations/${id}`, data);
  return response.data;
}

/**
 * Get recent activity logs for an organization
 */
export async function getPlatformOrganizationLogs(
  id: string,
  hours: number = 24
): Promise<OrgLogsResponse> {
  const response = await api.get<OrgLogsResponse>(`/platform/organizations/${id}/logs`, {
    params: { hours },
  });
  return response.data;
}

/**
 * Download organization activity logs as a Blob via the shared axios client
 * (so auth headers and the 401-refresh interceptor are applied consistently).
 */
export async function downloadOrgLogs(id: string): Promise<Blob> {
  const response = await api.get<Blob>(
    `/platform/organizations/${id}/logs/download`,
    { responseType: 'blob' }
  );
  return response.data;
}
